/**
 * Room ownership secrets live only in the browser that created the room.
 * The server stores a hash, so deleting a room requires proving possession of
 * the secret instead of claiming an owner name or flag.
 */
const STORAGE_KEY = 'chatsi.roomOwners.v1';

export interface OwnerStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

function storageOrDefault(storage?: OwnerStorage): OwnerStorage | null {
  if (storage) return storage;
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function loadAll(storage?: OwnerStorage): Record<string, string> {
  const target = storageOrDefault(storage);
  if (!target) return {};
  try {
    const parsed = JSON.parse(target.getItem(STORAGE_KEY) ?? '{}') as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const result: Record<string, string> = {};
    for (const [roomId, key] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof key === 'string' && key) result[roomId] = key;
    }
    return result;
  } catch {
    return {};
  }
}

function saveAll(owners: Record<string, string>, storage?: OwnerStorage) {
  const target = storageOrDefault(storage);
  if (!target) return;
  try {
    target.setItem(STORAGE_KEY, JSON.stringify(owners));
  } catch {
    // Private mode or a full quota: ownership stays session-only.
  }
}

export function rememberRoomOwner(roomId: string, ownerKey: string, storage?: OwnerStorage) {
  if (!roomId || !ownerKey) return;
  const owners = loadAll(storage);
  owners[roomId] = ownerKey;
  saveAll(owners, storage);
}

export function roomOwnerKey(roomId: string, storage?: OwnerStorage): string | null {
  if (!roomId) return null;
  return loadAll(storage)[roomId] ?? null;
}

export function hasRoomOwnerKey(roomId: string, storage?: OwnerStorage): boolean {
  return Boolean(roomOwnerKey(roomId, storage));
}

export function forgetRoomOwner(roomId: string, storage?: OwnerStorage) {
  const owners = loadAll(storage);
  if (!(roomId in owners)) return;
  delete owners[roomId];
  saveAll(owners, storage);
}
